import {
  BackTestQuestionType,
  TestQuestionData,
  TestQuestionFrontType,
  ValidationError,
} from "../../types";
import { DEFAULT_RESULT_VALUE } from "./constants";

export const getQuestionType = (questionType?: BackTestQuestionType,
  isMultiselect?: boolean) => {
  if (!questionType) return null;
  if (questionType !== BackTestQuestionType.Closed) return questionType;
  return isMultiselect ?
    TestQuestionFrontType.ClosedMany :
    TestQuestionFrontType.ClosedOne;
};

export const getAnswers = (questionType: TestQuestionFrontType, questionData?: TestQuestionData) => {
  if (!questionData) return [];

  switch (questionType) {
    case TestQuestionFrontType.ClosedOne:
    case TestQuestionFrontType.ClosedMany: {
      return questionData?.answer?.split("|");
    }
    case TestQuestionFrontType.Sequence: {
      return questionData?.currentAnswers?.split("|");
    }
    case TestQuestionFrontType.Matching: {
      return [ ...questionData?.leftAnswers?.split("|"), ...questionData?.rightAnswers?.split("|") ];
    }
    default: {
      return [];
    }
  }
};

export const getIsSendRequest = (
  questionType: TestQuestionFrontType,
  formattedUserAnswer: string,
  serverUserAnswer: string | undefined,
  userAnswer: unknown,
  serverUserComment: string | undefined,
  userComment: string,
) => {
  switch (questionType) {
    case TestQuestionFrontType.Open:
    case TestQuestionFrontType.ClosedMany:
    case TestQuestionFrontType.ClosedOne: {
      return (formattedUserAnswer !== DEFAULT_RESULT_VALUE &&
        serverUserAnswer !== formattedUserAnswer) || (serverUserComment !== userComment);
    }
    case TestQuestionFrontType.Sequence: {
      return true;
    }
    case TestQuestionFrontType.Survey: {
      return true;
    }
    case TestQuestionFrontType.Matching: {
      const userAnswerT = userAnswer as number[];
      const formatUserAnswer = userAnswerT?.map((a, i) => `${i + 1}:${a + 1}`);
      return formatUserAnswer?.join("|") !== serverUserAnswer;
    }
  }
};

export const getDefaultUserAnswer = (type: TestQuestionFrontType,
  rightAnswers?: string): unknown => {
  switch (type) {
    case TestQuestionFrontType.Open:
      return "";
    case TestQuestionFrontType.Matching:
      return new Array(rightAnswers?.split("|")?.length ?? 0).fill(null);
    default:
      return "";
  }
};

export const parseServerUserAnswer = (
  type: TestQuestionFrontType,
  serverAnswer: string,
  answers?: string[],
  rightAnswers?: string,
): unknown => {
  let userAnswerT: unknown;

  switch (type) {
    case TestQuestionFrontType.ClosedOne:
      userAnswerT = Number(serverAnswer);
      break;

    case TestQuestionFrontType.ClosedMany:
      userAnswerT = serverAnswer?.split("|").map(Number);
      break;

    case TestQuestionFrontType.Sequence:
      {
        const usedIndexes = new Set<number>();

        userAnswerT =
          serverAnswer
            ?.split("|")
            ?.map((answer) => {
              const index =
                answers?.findIndex((item, itemIndex) =>
                  item === answer && !usedIndexes.has(itemIndex)) ?? -1;

              if (index >= 0) {
                usedIndexes.add(index);
              }

              return index;
            })
            ?.filter((index) => index >= 0) ??
            [];
      }
      break;

    case TestQuestionFrontType.Open:
      userAnswerT = serverAnswer;
      break;

    case TestQuestionFrontType.Matching:
      userAnswerT =
        serverAnswer
          ?.split("|")
          ?.map((pair) => Number(pair.replace(/^[0-9]:/, "")) - 1) ??
          new Array(rightAnswers?.split("|")?.length ?? 0).fill(null);
      break;

    default:
      userAnswerT = "";
  }

  return userAnswerT;
};

export const getFormattedUserAnswer = (
  questionType: TestQuestionFrontType,
  userAnswer: unknown,
  answers: string[],
  currentQuestion?: TestQuestionData,
) => {
  let formattedUserAnswer: string = DEFAULT_RESULT_VALUE;

  switch (questionType) {
    case TestQuestionFrontType.ClosedMany: {
      const userAnswerT =
        Array.isArray(userAnswer) ?
          userAnswer as number[] :
          [];
      formattedUserAnswer = userAnswerT.join("|");
      break;
    }
    case TestQuestionFrontType.ClosedOne: {
      const userAnswerT = userAnswer as number;
      formattedUserAnswer = String(userAnswerT);
      break;
    }
    case TestQuestionFrontType.Sequence: {
      if (Array.isArray(userAnswer)) {
        const selectedAnswers = userAnswer.map((i) => answers[ i ]);
        const unSelectedAnswersLength = answers?.length - selectedAnswers?.length;

        /** если юзер выбрал не все ответы */
        if (unSelectedAnswersLength > 0) {
          answers?.forEach((answer, index) => {
            if (!userAnswer.includes(index)) {
              selectedAnswers.push(answer);
            }
          });
        }

        formattedUserAnswer = selectedAnswers.join("|");
      } else {
        formattedUserAnswer = answers?.join("|");
      }

      break;
    }
    case TestQuestionFrontType.Open: {
      formattedUserAnswer = (userAnswer ?? "") as string;
      break;
    }
    case TestQuestionFrontType.Survey: {
      formattedUserAnswer = (userAnswer ?? "") as string;
      break;
    }
    case TestQuestionFrontType.Matching: {
      if (Array.isArray(userAnswer)) {
        const answersArr = currentQuestion?.rightAnswers?.split("|") as string[];
        const selectedAnswers = userAnswer.map((i) => answersArr[ i ]);
        formattedUserAnswer = selectedAnswers.join("|");
      }
      break;
    }
  }
  return formattedUserAnswer;
};

export const validateAnswer = (
  value: string,
  questionType: TestQuestionFrontType,
  regex?: string,
  info?: string,
): ValidationError => {
  const trimmed = value.trim();

  if (
    (questionType === TestQuestionFrontType.Open ||
      questionType === TestQuestionFrontType.Survey) &&
      !trimmed
  ) {
    return {
      hasError: true,
      message: "Ответ не должен быть пустым",
    };
  }

  if (regex) {
    try {
      const pattern = new RegExp(regex);
      if (!pattern.test(trimmed)) {
        return {
          hasError: true,
          message: info || "Неверный формат",
        };
      }
    } catch (e) {
      console.warn(
        "Ошибка в регулярном выражении:", regex, e,
      );
    }
  }

  return {
    hasError: false,
    message: "",
  };
};
